import {Autocomplete, Box, Card, CardHeader, Chip, Dialog, IconButton, TextField, Tooltip} from '@mui/material'
import {SectorComercialViewDTO, SectorComercialViewDTOFields} from './types'
import {HttpSectorComercialABM} from './api'
import {GridActionsCellItem} from '@mui/x-data-grid'
import {useEffect, useMemo, useState} from 'react'
import GroupsIcon from '@mui/icons-material/Groups'
import EditIcon from '@mui/icons-material/Edit'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import EditSectorComercial from './EditSectorComercial'
import EditSectorComercialForm from './EditSectorComercialForm'
import {useConfirmationDialog} from 'src/hooks/useConfirmationDialog'
import toast from 'react-hot-toast'
import {ItecColFilters, ItecDataGrid, ItecGridColsDef as GridColsDef, useItecDataGrid} from 'src/lib/itaptec/datagrid/src'
import CachedIcon from '@mui/icons-material/Cached'
import {FilterAltOff, Search} from '@mui/icons-material'
import {WrapperTable} from '../components'
import {CountryAvatar} from "../../../../../components/icons/CountryAvatar"
import {usePaisLogo} from 'src/hooks/usePaisLogo'
import {EntityWithIdAndDescriptionFields, PaisLogoFields, schemaSelectionOption} from 'src/types'
import {z} from 'zod'
import {Controller} from 'react-hook-form'
import {ControlledAutocomplete} from 'src/components/forms/ControlledAutocomplete'
import {HttpUnidadNegocio} from '../unidades-negocio/api'

const schemaFiltro = z.object({
  nombre: z.string().optional(),
  codsPais: z.array(schemaSelectionOption).optional(),
  codsUnidadNegocio: z.array(schemaSelectionOption).optional()
})

type SchemaFiltro = z.infer<typeof schemaFiltro>

function TableSectoresComerciales() {
  const {askConfirmation} = useConfirmationDialog()
  const [selectedSectorComercial, setSelectedSectorComercial] = useState<SectorComercialViewDTO>()
  const [selectedSectorEdit, setSelectedSectorEdit] = useState<SectorComercialViewDTO>()
  const {paisLogos} = usePaisLogo()
  const [allUnidades, setAllUnidades] = useState<{id: number; descripcion: string; codPais: number | null}[]>([])

  useEffect(() => {
    HttpUnidadNegocio.getAll().then(res =>
      setAllUnidades(res.map(s => ({ id: s.id, descripcion: s.nombreUnidadNegocio, codPais: s.codPais })))
    )
  }, [])

  const columnas: GridColsDef<SectorComercialViewDTO>[] = [
    {
      headerName: 'Unidad de Negocio',
      field: SectorComercialViewDTOFields.NombreUnidadNegocio,
      flex: 2
    },
    {
      headerName: 'Sector Comercial',
      field: SectorComercialViewDTOFields.Nombre,
      flex: 3
    },
    {
      headerName: 'País',
      field: SectorComercialViewDTOFields.NombrePais,
      flex: 1,
      renderCell: ({row: sector}) => (
        <Chip
          label={sector[SectorComercialViewDTOFields.NombrePais]}
          size='small'
          variant='outlined'
          avatar={<CountryAvatar countryCode={sector[SectorComercialViewDTOFields.CodPais]}/>}
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({row}) => [
        <GridActionsCellItem
          key={1}
          onClick={() => {
            setSelectedSectorEdit(row)
          }}
          icon={
            <Tooltip title='Editar'>
              <EditIcon color='info'/>
            </Tooltip>
          }
          label='Editar'
          showInMenu={true}
        />,
        <GridActionsCellItem
          key={2}
          onClick={() => {
            setSelectedSectorComercial(row)
          }}
          icon={
            <Tooltip title='Grupos'>
              <GroupsIcon color='primary'/>
            </Tooltip>
          }
          label='Grupos'
          showInMenu={true}
        />,
        <GridActionsCellItem
          key={3}
          onClick={() =>
            askConfirmation(() => {
              const promise = HttpSectorComercialABM.deactivate(row.id)
              toast
                .promise(promise, {
                  success: 'Inactivado',
                  error: 'No se pudo inactivar',
                  loading: 'Inactivando'
                })
                .then(() => refetch())
            })
          }
          icon={
            <Tooltip title='Inactivar'>
              <PersonRemoveIcon color='error'/>
            </Tooltip>
          }
          label='Inactivar'
          showInMenu={true}
        />
      ]
    }
  ]

  // Filtros dummy para que el hook cree el form reactivo
  const filtros: ItecColFilters<SchemaFiltro> = {
    [SectorComercialViewDTOFields.Nombre]: {
      type: 'text',
      name: 'nombre',
      props: { debounceTime: 500, size: 'small' }
    },
    [SectorComercialViewDTOFields.NombrePais]: {
      type: 'multiselect',
      name: 'codsPais',
      props: { getOptions: () => Promise.resolve([]), size: 'small' }
    },
    [SectorComercialViewDTOFields.NombreUnidadNegocio]: {
      type: 'multiselect',
      name: 'codsUnidadNegocio',
      props: { getOptions: () => Promise.resolve([]), size: 'small' }
    }
  }

  const {
    datagridProps,
    control: {
      query: {refetch},
      form: filterForm
    }
  } = useItecDataGrid<SectorComercialViewDTO, SchemaFiltro>({
    pagination: false,
    columns: columnas,
    filters: filtros,
    getEntities: (filter) =>
      HttpSectorComercialABM.getSectoresComerciales().then(data => {
        if (!filter) return data

        let filtered = data

        const nombre = filter.nombre
        if (nombre) {
          const lower = nombre.toLowerCase()
          filtered = filtered.filter(s =>
            s[SectorComercialViewDTOFields.Nombre]?.toLowerCase().includes(lower)
          )
        }

        const codsPais = filter.codsPais
        if (codsPais && codsPais.length > 0) {
          const paisValues = codsPais.map(p => (p as any).id ?? p.value)
          filtered = filtered.filter(s => paisValues.includes(s[SectorComercialViewDTOFields.CodPais]))
        }

        const codsUnidadNegocio = filter.codsUnidadNegocio
        if (codsUnidadNegocio && codsUnidadNegocio.length > 0) {
          const unValues = codsUnidadNegocio.map(u => (u as any).id ?? u.value)
          filtered = filtered.filter(s =>
            s[SectorComercialViewDTOFields.CodUnidadNegocio] !== null &&
            unValues.includes(s[SectorComercialViewDTOFields.CodUnidadNegocio]!)
          )
        }

        return filtered
      }),
    density: 'compact'
  })

  // Quitar menus de filtro de los headers
  const cleanDatagridProps = {
    ...datagridProps,
    columns: datagridProps.columns.map(col => ({
      ...col,
      disableColumnMenu: true,
      renderHeader: undefined
    })),
    slots: undefined
  }

  const handleClearFilters = () => {
    if (!filterForm) return
    filterForm.reset({
      nombre: undefined,
      codsPais: undefined,
      codsUnidadNegocio: undefined
    })
  }

  return (
    <WrapperTable>
      <Card>
        <CardHeader
          title='Administración de sectores comerciales'
          action={
            <IconButton disabled={datagridProps.loading} onClick={() => refetch()}>
              <CachedIcon/>
            </IconButton>
          }
        />

        {/* Barra de filtros */}
        {filterForm && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mx: 2,
              mb: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover'
            }}
          >
            <Controller
              control={filterForm.control}
              name='nombre'
              render={({field}) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label='Nombre'
                  size='small'
                  sx={{minWidth: 180, flex: 1}}
                  InputProps={{
                    startAdornment: <Search fontSize='small' sx={{mr: 0.5, color: 'text.secondary'}}/>
                  }}
                />
              )}
            />
            <Box sx={{minWidth: 180, flex: 1}}>
              <ControlledAutocomplete
                control={filterForm.control}
                name='codsPais'
                label='País'
                                loadOptions={() =>
                  Promise.resolve(paisLogos).then(res =>
                    res.map(s => ({
                      id: s.id,
                      descripcion: s[EntityWithIdAndDescriptionFields.Description]
                    }))
                  )
                }
                multiple
              />
            </Box>
            <Box sx={{minWidth: 200, flex: 1}}>
              <Controller
                control={filterForm.control}
                name='codsUnidadNegocio'
                render={({field}) => {
                  const selectedPaises = filterForm.watch('codsPais')
                  const paisIds = (selectedPaises ?? []).map((p: any) => p.id ?? p.value)
                  const unidadesFiltradas = paisIds.length > 0
                    ? allUnidades.filter(u => u.codPais !== null && paisIds.includes(u.codPais))
                    : []
                  const disabled = paisIds.length === 0

                  return (
                    <Autocomplete
                      multiple
                      disabled={disabled}
                      options={unidadesFiltradas}
                      value={(field.value ?? [] as any[]).filter((v: any) => unidadesFiltradas.some(u => u.id === (v.id ?? v.value))) as any}
                      onChange={(_, data) => field.onChange(data)}
                      getOptionLabel={(option: any) => option.descripcion ?? ''}
                      isOptionEqualToValue={(option: any, value: any) => option.id === (value.id ?? value.value)}
                      size='small'
                      renderInput={(params) => (
                        <TextField {...params} label='Unidad de Negocio' variant='outlined' />
                      )}
                      ChipProps={{color: 'primary', size: 'small'}}
                    />
                  )
                }}
              />
            </Box>
            <Tooltip title='Limpiar filtros'>
              <IconButton size='small' onClick={handleClearFilters}>
                <FilterAltOff fontSize='small'/>
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <ItecDataGrid {...cleanDatagridProps} />
      </Card>
      <>
        <Dialog
          open={!!selectedSectorComercial}
          fullWidth
          maxWidth='lg'
          keepMounted={false}
          sx={{maxHeight: '100%'}}
        >
          {selectedSectorComercial && (
            <EditSectorComercial
              onClose={() => {
                setSelectedSectorComercial(undefined)
              }}
              sector={selectedSectorComercial}
            />
          )}
        </Dialog>
        <Dialog open={!!selectedSectorEdit} fullWidth maxWidth='sm' keepMounted={false}>
          {selectedSectorEdit && (
            <EditSectorComercialForm
              onClose={() => {
                setSelectedSectorEdit(undefined)
                refetch()
              }}
              sector={selectedSectorEdit}
            />
          )}
        </Dialog>
      </>
    </WrapperTable>
  )
}

export default TableSectoresComerciales
